import { AsgardCloudBaseService } from '../asgard-cloud-base.service';
import { Injectable } from '@angular/core';
import { CloudData } from '../metadata/CloudData';
import { MimeTypesEnum, ToastrActionEnum, ToastrStringsEnum } from '../../../utils/enums';
import { ToastrDialogService } from '../../toastr-dialog.service';
import { TabService } from '../../tab.service';
import { FileConvertorService } from '../../file-convertor/file-convertor-service';
import { ModalWindowService } from '../../ui/modal-window.service';
import { DocumentManagerService } from '../../document/document-manager.service';
import { BlobToPdfService } from '../../blob-to-pdf.service';
import { ExtraCopyDialogComponent } from '../../../components/dialogs/extra-copy-dialog/extra-copy-dialog.component';
import { TranslateService } from '@ngx-translate/core';
// import { CommonMessageDialogComponent } from '../../../components/dialogs/common-message-dialog/common-message-dialog.component';
import { SaveStatusEnum } from '../enums/save-status.enum';
import { isK3000domain, parseExtenstion } from '../../../core/common';
import { FileData } from '../../../../models/FileData';
import { DriveMetadata } from '../metadata/DriveMetadata';
import { FileCopyInfo } from '../models/file-copy-info';
import { CloudFoldersEnum } from '../../../utils/enums/cloud-folders.enum';
import { CloudException, CloudExceptionEnum } from '../models/exceptions';
import { DropboxApiService, DropboxUploadMode } from './dropbox-api.service';
import { files } from 'dropbox';
import { getMimeTypeEnumFromExt, validateMimetypes } from '../common';
import { CloudRecentFilesService } from '../cloud-recent-files.service';
import { CloudServiceEnum, MetadataTypesEnum } from '../enums';
import { DropboxMetaEntryModel } from '../ApiModels/DropboxMetaEntryModel';
import { ExamModeRestrictionsService } from '../../exam/exam-mode-restrictions.service';
import { MatDialog } from '@angular/material/dialog';
import { AccountService } from '../../auth/account.service';
import { LicenseService } from '../../license.service';
import { OpenFileService } from '../../open-file.service';
import { AsgardDocEpub } from 'src/app/core/documents/asgard-doc-epub';
import { EpubFileService } from '../../epub/epub-file.service';

type ItemMetadataReference = files.FileMetadataReference | files.FolderMetadataReference | files.DeletedMetadataReference;

interface DropboxFileUploadModel {
  file: File;
  path: string;
  filename: string;
  params: {
    convert: boolean;
    ocr: boolean;
  };
  fileId?: string;
}

interface DropboxDownloadMetadata extends files.FileMetadata {
  fileBlob?: any;
}

interface DropboxSharingInfo extends files.FileSharingInfo {
  shared_folder_id: string;
}

@Injectable({
  providedIn: 'root'
})
export class AsgardCloudDropboxService extends AsgardCloudBaseService {

  private dropboxMetadataList: Array<ItemMetadataReference | DropboxMetaEntryModel> = [];
  private file: File;
  private uploadInfo: DropboxFileUploadModel;

  constructor(
    tabService: TabService,
    convertorService: FileConvertorService,
    modalService: ModalWindowService,
    docMgrService: DocumentManagerService,
    toastrService: ToastrDialogService,
    blobToPdfService: BlobToPdfService,
    epubFileService: EpubFileService,
    translateService: TranslateService,
    examModeService: ExamModeRestrictionsService,
    dialog: MatDialog,
    accountService: AccountService,
    licenseService: LicenseService,
    openFileService: OpenFileService,
    private readonly dropboxApi: DropboxApiService,
    private readonly cloudRecentFilesService: CloudRecentFilesService,
  ) {
    super(
      tabService, convertorService, modalService, docMgrService,
      toastrService, blobToPdfService, epubFileService, translateService, examModeService,
      dialog, accountService, licenseService, openFileService
    );
  }

  async getFiles(currentFolder: string | undefined, showAllFiles: boolean,
    dialogStatus: SaveStatusEnum, folderToShow: CloudFoldersEnum): Promise<CloudData[]> {

    if (folderToShow === CloudFoldersEnum.Recent) {
      const files = await this.cloudRecentFilesService.getFiles(CloudServiceEnum.DropBox) as files.FileMetadataReference[];
      return this.createCloudDataList(files, showAllFiles, dialogStatus, folderToShow);
    }

    if (currentFolder === undefined || currentFolder === this.ROOT_FOLDER) {
      currentFolder = '';
    }

    return this.dropboxApi.getFiles(currentFolder)
      .then((dropboxFiles) => {
        return this.createCloudDataList(dropboxFiles, showAllFiles, dialogStatus, folderToShow);
      });
    // .catch((error) => {
    //   console.error(error);
    //   this.displayGeneralError();
    //   return [] as CloudData[];
    // });
  }

  async newFolder(name: string, cloudData: CloudData | null, autorename: boolean = true): Promise<files.CreateFolderResult | void> {
    let folderName = `/${name.trim()}`;

    if (cloudData && cloudData.id !== this.ROOT_FOLDER) {
      folderName = `${cloudData.path}/${name.trim()}`;
    }

    return await this.dropboxApi.newFolder(folderName, autorename).catch((error) => {
      console.error(error);
      this.displayGeneralError();
      return;
    });
  }


  async copyOrMoveFile(fileCopyInfo: FileCopyInfo): Promise<void> {
    if (fileCopyInfo.fileToCopy) {
      const parentId = fileCopyInfo.parentToCopy ? fileCopyInfo.parentToCopy.id : '';
      const fromPath = fileCopyInfo.fileToCopy.parents ? fileCopyInfo.fileToCopy.parents : fileCopyInfo.fileToCopy.path;
      const toPath = parentId + '/' + fileCopyInfo.filename;

      if (fileCopyInfo.copy) {
        await this.dropboxApi.copyFile(fromPath, toPath, true).catch((error) => {
          console.error(error);
          this.displayError(new CloudException(CloudExceptionEnum.fileCopy));
        });
      } else {
        this.checkFileMovingPath(fromPath, toPath);
        await this.dropboxApi.moveFile(fromPath, toPath, true).catch((error) => {
          console.error(error);
          this.displayError(new CloudException(CloudExceptionEnum.fileMove));
        });
      }
    }
  }

  async deleteFile(file: CloudData): Promise<files.DeleteResult | void> {
    try {
      return await this.dropboxApi.deleteFile(file.path);
    } catch (err) {
      this.displayError(new CloudException(CloudExceptionEnum.fileDelete));
    }
  }

  async updateFile(file: CloudData, params: Partial<DriveMetadata>): Promise<void> {
    // remove old filename from metadata path, add new one and re-add extension in name
    const newName = this.splitPopJoin(file.path) + '/' + params.name;

    this.cloudRecentFilesService.removeFileFromRecent(file.id, CloudServiceEnum.DropBox);

    const result = await this.dropboxApi.moveFile(file.id, newName, true).catch((error) => {
      console.error(error);
      this.displayGeneralError();
    });

    if (result && result.metadata) {
      this.cloudRecentFilesService.addFileToRecent(result.metadata as files.FileMetadataReference, CloudServiceEnum.DropBox);
    }
  }

  private splitPopJoin(str: string): string {
    const splitArray = str.split('/');
    splitArray.pop();
    return splitArray.join('/');
  }

  async getFileFromCloud(fileId: string, dialogStatus: SaveStatusEnum, paneIndex: number): Promise<void> {
    const metadata = this.getMetadata(fileId);
    if (!metadata) {
      return;
    }

    this.getFile(fileId, dialogStatus)
      .then((file) => {
        this.file = file;
        this.processFiles(this.file, dialogStatus, metadata, paneIndex);
        this.cloudRecentFilesService.addFileToRecent(metadata as files.FileMetadataReference, CloudServiceEnum.DropBox);
      })
      .catch((error) => {
        console.error(error);
        if (error !== 'different user') {
          this.displayGeneralError();
        }
      });
  }

  getMetadata(fileId: string): ItemMetadataReference | null {
    for (const item of this.dropboxMetadataList) {
      const file = item as files.FileMetadataReference;
      const folder = item as files.FolderMetadataReference;

      if (file && file.id === fileId) {
        return file as ItemMetadataReference;
      } else if (folder && folder.id === fileId) {
        return folder as ItemMetadataReference;
      }
    }

    return null;
  }

  upload(fileData: FileData): void {
    this.uploadInfo = {
      file: fileData.file,
      path: fileData.path,
      filename: fileData.filename,
      params: {
        convert: false,
        ocr: false
      },
      fileId: (this.dropboxMetadata as files.FileMetadataReference)?.id || undefined,
    };

    if (fileData.file.type === MimeTypesEnum.Mp3) {
      this.uploadFile(true); // autorename if multiple clips of same doc are made
      return;
    } else if (!this.getSaveAs()) {
      if (this.dropboxMetadata !== undefined && this.dropboxMetadata !== null && this.dropboxMetadata.name === fileData.filename) {
        this.uploadFile(false);
      } else {
        this.uploadFile(true);
      }
    } else {
      for (const fileMeta of this.dropboxMetadataList) {
        let path = fileData.path.toLowerCase();
        path = this.checkForFullDropboxPath(path, fileData.filename);

        if (fileMeta.name === fileData.filename && (fileMeta.path_lower === path.toLowerCase())) {
          this.modalService.createModalWindow(ExtraCopyDialogComponent); // This dialog triggers and 'disrupts' flow
          return;
        }
      }
      this.uploadFile(false);
    }
  }

  async uploadFile(autorename: boolean): Promise<void> {
    const toastrKey = this.getTranslatedTitle();
    this.toastrService.showToastrByType(ToastrActionEnum.Info, ToastrStringsEnum.Uploading, true, toastrKey);

    this.uploadInfo.path = this.checkForFullDropboxPath(this.uploadInfo.path, this.uploadInfo.filename);

    try {
      if (autorename) {
        this.dropboxMetadata = await this.dropboxApi.uploadFile(
          this.uploadInfo.file,
          autorename,
          this.uploadInfo.path,
          undefined
        ) as files.FileMetadataReference;

        // if autorenamed, set autorenamed title
        if (this.uploadInfo.file.type !== MimeTypesEnum.Mp3 && this.tabService.activeTab && this.dropboxMetadata) {
          this.tabService.activeTab.setTabTitle(this.dropboxMetadata.name);
        }
      } else {
        if (this.uploadInfo.fileId) {
          this.cloudRecentFilesService.removeFileFromRecent(this.uploadInfo.fileId, CloudServiceEnum.DropBox);
        }

        this.dropboxMetadata = await this.dropboxApi.uploadFile(
          this.uploadInfo.file,
          autorename,
          this.uploadInfo.path,
          DropboxUploadMode.Overwrite
        ) as files.FileMetadataReference;
      }

      if (this.dropboxMetadata) {
        (this.dropboxMetadata as DropboxMetaEntryModel).type = MetadataTypesEnum.DropboxMetaEntryModel;
      }

      this.cloudRecentFilesService.addFileToRecent(this.dropboxMetadata, CloudServiceEnum.DropBox);

      this.fillMetadata(this.dropboxMetadata);

      this.toastrService.showToastrByType(ToastrActionEnum.Success, ToastrStringsEnum.UploadDone, false, toastrKey);

      if (this.tabService.activeTab && this.tabService.activeTab.isOriginallyKES && this.uploadInfo.file.type !== MimeTypesEnum.Mp3) {
        // this.tabService.activeTab.isDedicon ?
        //   this.modalService.createModalWindow(CommonMessageDialogComponent, { messageKey: 'COMMONMESSAGEDIALOG.PDF_SEK_SAVE_SUCCESS' }) :
        //   this.modalService.createModalWindow(CommonMessageDialogComponent, { messageKey: 'COMMONMESSAGEDIALOG.PDF_KES_SAVE_SUCCESS' });

        const messageKey = this.tabService.activeTab.isDedicon ? 'COMMONMESSAGEDIALOG.PDF_SEK_SAVE_SUCCESS' : 'COMMONMESSAGEDIALOG.PDF_KES_SAVE_SUCCESS';
        this.toastrService.showToaster(ToastrActionEnum.Success, this.translateService.instant(messageKey));
      }
    } catch (err) {
      this.toastrService.hideToastr(toastrKey);

      if (err.status === 409 || err.status === 507) {
        this.displayError(new CloudException(err.status));
      } else {
        this.displayError(new CloudException(CloudExceptionEnum.fileUpload));
      }
    }
  }

  private checkForFullDropboxPath(path: string, filename: string): string {
    if (path === this.ROOT_FOLDER) {
      path = '';
    }

    return path.toLowerCase().indexOf(filename.toLowerCase()) === -1 ? (path + '/' + filename) : path;
  }

  checkIfFileIsTrashedOrGone(): void {
    throw new Error('Not Implemented');
  }

  saveAudiofile(audiofile: File, currentFolder: CloudData | null): void {
    this.uploadToCloud(currentFolder, audiofile.name, audiofile);
  }

  async uploadToCloud(path: CloudData | null, filename: string, file: File | null = null): Promise<void> {
    if (!file || file.type !== MimeTypesEnum.Mp3) {
      if (this.tabService.activeTab && (filename === undefined || filename === '')) {
        filename = this.tabService.activeTab.title;
        this.dropboxMetadata = this.tabService.activeTab.dropboxMetadata;
      }

      if (!file) {
        if (this.docMgrService.currentDoc instanceof AsgardDocEpub) {
          file = await this.epubFileService.exportWithAnnotations();
        } else {
          file = await this.blobToPdfService.getCurrentPDF();
        }

        // getCurrentPDF() can return null. if it does, do not continue.
        if (!file) {
          this.displayGeneralError();
          return;
        }
      }
    }

    let pathId: string | undefined = path ? path.id : this.ROOT_FOLDER;

    // If audiomaker && destination is root, relay to or create audiomaker folder
    if (file && file.type === MimeTypesEnum.Mp3 && pathId === this.ROOT_FOLDER) {
      pathId = await this.createOrAddAudiomakerFolder();
    }

    if (pathId) {
      const fileData: FileData = this.checkFilenameBeforeUpload(file, filename, pathId);

      // Also add .pdf to the path, or it will create a folder with the file in instead of the file.
      if (pathId === this.ROOT_FOLDER && fileData.file.type === MimeTypesEnum.ApplicationPdf) {
        fileData.filename.indexOf('.pdf') < 0 ? fileData.path = '/' + fileData.filename + '.pdf' : fileData.path = '/' + fileData.filename;
      }

      if (file && file.type !== MimeTypesEnum.Mp3) {
        if (this.tabService.activeTab && fileData.filename !== this.tabService.activeTab.title) {
          this.overrideFilenameParams(fileData.filename);
        }
      }

      this.upload(fileData);
    }
  }

  async createOrAddAudiomakerFolder(): Promise<string | undefined> {
    const foldername = isK3000domain() ? this.K3000_AUDIOMAKER_FOLDER : this.ALINEA_AUDIOMAKER_FOLDER;

    // Check for existing folder and create one if necessary
    return this.dropboxApi.getMetaData(foldername)
      .then((folderMetadata) => {
        return folderMetadata.path_lower;
      })
      .catch((error) => {
        console.error(error);
        return this.newFolder(foldername, { id: this.ROOT_FOLDER } as CloudData, false);
      })
      .then(() => {
        return this.dropboxApi.getMetaData(foldername);
      })
      .then((folderMetadata) => {
        return folderMetadata.path_lower;
      })
      .catch((error) => {
        console.error(error);
        this.displayGeneralError();
        return '';
      });
  }

  getFileFromId(fileId: string): Promise<File> {
    return this.getFile(fileId, SaveStatusEnum.ExamModeFilePicker)
      .then((file) => {
        this.toastrService.hideToastr(file.name);
        return Promise.resolve(file);
      })
      .catch((error) => {
        console.error(error);
        this.displayGeneralError();
        const metadata = this.getMetadata(fileId);
        this.toastrService.hideToastr(metadata === null ? '' : metadata.name);
        return Promise.reject();
      });
  }

  private async createCloudDataList(response: files.ListFolderResult | files.FileMetadataReference[], showAllFiles: boolean,
    dialogStatus: SaveStatusEnum, folderToShow: CloudFoldersEnum): Promise<CloudData[]> {
    this.dropboxMetadataList = [];
    let cloudDataList: CloudData[] = [];

    const files = response.hasOwnProperty('entries') ? (response as files.ListFolderResult).entries : response as files.FileMetadataReference[];

    files.forEach(item => {
      const cloudData = this.createCloudData(item);

      if (cloudData) {
        if (item['.tag'] === 'file') {
          (item as DropboxMetaEntryModel).type = MetadataTypesEnum.DropboxMetaEntryModel;
        }

        this.dropboxMetadataList.push(item);
        cloudDataList.push(cloudData);
      }
    });

    cloudDataList = this.filterSharedOrUnsharedFiles(cloudDataList, folderToShow);

    if (!showAllFiles || dialogStatus === SaveStatusEnum.Ocr) {
      cloudDataList = this.filterUnsupportedFiles(cloudDataList, dialogStatus);
    }

    return cloudDataList;
  }

  private filterSharedOrUnsharedFiles(datalist: CloudData[], folderToShow: CloudFoldersEnum): CloudData[] {
    return folderToShow === CloudFoldersEnum.Shared ? datalist.filter(f => f.isShared) : datalist;
  }

  private createCloudData(item: ItemMetadataReference): CloudData | null {
    if (item['.tag'] === 'file') {
      return this.createCloudDataFromFileMetadata(item as files.FileMetadataReference);
    } else if (item['.tag'] === 'folder') {
      return this.createCloudDataFromFolderMetadata(item as files.FolderMetadataReference);
    } else if (item['.tag'] === 'deleted') {
      return this.createCloudDataFromDeletedMetadata(item as files.DeletedMetadataReference);
    }

    return null;
  }

  private createCloudDataFromFileMetadata(file: files.FileMetadataReference): CloudData {
    let sharingInfoParentSharedFolderId: string = '';

    // NOTE: example of dropbox sharing_info
    // {
    //   "read_only":false,
    //   "shared_folder_id":"6814169904",
    //   "traverse_only":false,
    //   "no_access":false
    // }
    if (!!file.sharing_info) {
      sharingInfoParentSharedFolderId = (file.sharing_info as DropboxSharingInfo).shared_folder_id;
    }

    const extension = parseExtenstion(file.name);

    return {
      id: file.id,
      name: file.name,
      extension,
      mimeType: getMimeTypeEnumFromExt(file.name, file['.tag']),
      modifiedTime: file.client_modified,
      size: `${file.size || ''}`,
      isShared: !!sharingInfoParentSharedFolderId || !!file.parent_shared_folder_id,
      path: file.path_lower,
      parents: null,
    } as unknown as CloudData;
  }

  private createCloudDataFromFolderMetadata(folder: files.FolderMetadataReference): CloudData {
    const extension = parseExtenstion(folder.name);
    const mimeType = getMimeTypeEnumFromExt(folder.name, folder['.tag']);

    return {
      id: folder.id,
      name: folder.name,
      extension: mimeType !== MimeTypesEnum.Folder ? extension : '',
      mimeType,
      modifiedTime: new Date().toJSON(),
      size: '0',
      isShared: !!folder.shared_folder_id || !!folder.parent_shared_folder_id,
      path: folder.path_lower,
      parents: null,
    } as unknown as CloudData;
  }

  private createCloudDataFromDeletedMetadata(deletedFile: files.DeletedMetadataReference): CloudData {
    const extension = parseExtenstion(deletedFile.name);

    return {
      id: '',
      name: deletedFile.name,
      extension,
      mimeType: getMimeTypeEnumFromExt(deletedFile.name, deletedFile['.tag']),
      modifiedTime: new Date().toJSON(),
      size: '0',
      isShared: false,
      path: deletedFile.path_lower,
      parents: null,
    } as unknown as CloudData;
  }

  private validateFileSupport(metadata: ItemMetadataReference, dialogStatus: SaveStatusEnum): boolean {
    const mimeType = getMimeTypeEnumFromExt(metadata.name, metadata['.tag']);
    const vMetadata = { fileName: metadata.name, mimeType };
    const isOcr = dialogStatus === SaveStatusEnum.Ocr;
    return validateMimetypes(vMetadata, isOcr, true);
  }

  private displayGeneralError(): void {
    this.displayError(new CloudException(CloudExceptionEnum.fileRetrieval));
  }

  private async getFile(fileId: string, dialogStatus: SaveStatusEnum): Promise<File> {
    const metadata = this.getMetadata(fileId);
    if (!metadata) {
      return Promise.reject();
    }

    if (!this.validateFileSupport(metadata, dialogStatus)) {
      // this.modalService.createModalWindow(CommonMessageDialogComponent, { messageKey: 'COMMONMESSAGEDIALOG.NOT_SUPPORTED' });
      this.toastrService.showToaster(ToastrActionEnum.Error, this.translateService.instant('COMMONMESSAGEDIALOG.NOT_SUPPORTED'));
      return Promise.reject();
    }

    const metadataPath = metadata.path_lower;
    if (!metadataPath) {
      return Promise.reject();
    }

    const userId = this.accountService.userId;
    this.toastrService.showToastrByType(ToastrActionEnum.Info, ToastrStringsEnum.Downloading, true, metadata.name);

    return this.dropboxApi.downloadFile(metadataPath)
      .then((metadataWithBlob: DropboxDownloadMetadata) => {
        // if it is still the same user that is logged in
        const currentUserId = this.accountService.userId;
        if (userId === currentUserId) {
          this.dropboxMetadata = metadata;
          const blob = metadataWithBlob.fileBlob;
          const fileExtension = getMimeTypeEnumFromExt(metadataWithBlob.name, metadataWithBlob['.tag']);
          const file = this.createFileFromBlob(blob, fileExtension, metadataWithBlob.name);
          return Promise.resolve(file);
        }

        // hide de downloading toastr again
        this.toastrService.hideToastr(metadata.name);
        return Promise.reject('different user');
      });
  }
}
